import { Moon, MonitorSmartphone, Sun } from 'lucide-react';
import type { ThemeMode } from '@eombor/ui';
import { cn } from '@eombor/ui';
import { setLocale, t, type Locale } from '../i18n/index.js';
import { useLocale } from '../i18n/useLocale.js';
import { useThemeMode } from '../theme/useThemeMode.js';
import { ChoiceGroup } from '../ui/ChoiceGroup.js';

/** Bayroq fayllari — `LocalePicker` bilan bir xil to'plam. */
const FLAGS: Record<Locale, string> = {
  uz: '/assets/flags/UZ.svg',
  ru: '/assets/flags/RU.svg',
  en: '/assets/flags/EN.svg',
};

const LOCALES: readonly Locale[] = ['uz', 'ru', 'en'];

const THEMES: readonly ThemeMode[] = ['light', 'dark', 'system'];

const themeIcon = (mode: ThemeMode) => {
  if (mode === 'light') return <Sun size={14} aria-hidden />;
  if (mode === 'dark') return <Moon size={14} aria-hidden />;
  return <MonitorSmartphone size={14} aria-hidden />;
};

export interface AppSettingsProps {
  className?: string;
}

/**
 * TIL VA KO'RINISH — bitta blok, ikkala manba ham o'z store'idan.
 *
 * Blok alohida komponent, chunki uni ikki joy chizishi mumkin va
 * ikkalasi ham BIR XIL holatni ko'rsatishi shart: til modul darajasida
 * (`shared/i18n`), tema ham (`shared/theme`) — ikkalasi
 * `useSyncExternalStore` orqali o'qiladi. Nusxa ko'chirilgan holat
 * bo'lganda sheet ichida almashtirilgan tema header'da eski holicha
 * qolardi.
 *
 * Tanlangandan keyin blok YOPILMAYDI: o'zgarish joyida ko'rinadi
 * (yorliqlar yangi tilga o'tadi, tema darhol almashadi) va odam natijani
 * ko'rib turib yopadi. Darhol yopilsa, u nima o'zgarganini ekran
 * yopilayotganda ilg'ashi kerak bo'lardi.
 */
export const AppSettings = ({ className }: AppSettingsProps) => {
  const locale = useLocale();
  const [themeMode, setThemeMode] = useThemeMode();

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <SettingRow label={t('locale.label')}>
        <ChoiceGroup
          label={t('locale.label')}
          value={locale}
          onChange={setLocale}
          className="w-full sm:w-auto"
          options={LOCALES.map((code) => ({
            value: code,
            label: code.toUpperCase(),
            icon: (
              /* Bayroq — KICHIK: u tanlovni tezlashtiradigan belgi,
                 sozlama qatorining bezagi emas. */
              <img src={FLAGS[code]} alt="" aria-hidden className="size-4 rounded-full" />
            ),
          }))}
        />
      </SettingRow>

      <SettingRow label={t('settings.appearance')}>
        <ChoiceGroup
          label={t('settings.appearance')}
          value={themeMode}
          onChange={setThemeMode}
          className="w-full sm:w-auto"
          options={THEMES.map((mode) => ({
            value: mode,
            label: t(`theme.${mode}`),
            icon: themeIcon(mode),
          }))}
        />
      </SettingRow>
    </div>
  );
};

interface SettingRowProps {
  label: string;
  children: React.ReactNode;
}

/**
 * Yorliq chapda, boshqaruv o'ngda — tor ekranda esa ustma-ust.
 *
 * Yorliq `aria-hidden`: uning matni `ChoiceGroup` ning `aria-label` ida
 * allaqachon bor va ekran o'qigich uni ikki marta o'qishi kerak emas.
 */
const SettingRow = ({ label, children }: SettingRowProps) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <span aria-hidden className="text-sm font-medium text-text-muted">
      {label}
    </span>
    {children}
  </div>
);
